import cloneDeep from 'lodash-es/cloneDeep';

type AnyObject = { [prop: string]: any };

// Remove duplicates from array of objects
export const arrayUniqueByKey = <T extends AnyObject, KEY extends keyof T>(array: T[], key: KEY): T[] => [
  ...new Map(array.map((item) => [item[key], item])).values()
];

interface FilterNotEmptyOptions {
  removeEmptyStrings?: boolean;
  removeEmptyArrays?: boolean;
}
export function filterNotEmpty<MODEL>(
  values?: (MODEL | undefined | null | false)[],
  options?: FilterNotEmptyOptions
): MODEL[] {
  return (values ?? []).filter((value) => notEmpty(value, options)) as MODEL[];
}

function notEmpty<MODEL>(value: MODEL | null | undefined | false, options?: FilterNotEmptyOptions): value is MODEL {
  return !(
    value === null ||
    value === undefined ||
    value === false ||
    (options?.removeEmptyStrings && typeof value === 'string' && value.trim() === '') ||
    (options?.removeEmptyArrays && Array.isArray(value) && value?.length === 0)
  );
}

export function getEntryBy<T extends AnyObject, KEY extends keyof T & string = 'uid'>(
  array: T[],
  uniqueValue: string,
  // @ts-ignore assumption T contains uid
  uniqueValueKey: KEY = 'uid'
): T | undefined {
  return array.find((entry) => entry[uniqueValueKey] === uniqueValue);
}

function getIndexBy<T extends AnyObject>(uniqueValue: string, elements: T[], uniqueValueKey: string = 'uid'): number {
  return elements.findIndex((entry) => entry?.[uniqueValueKey] === uniqueValue);
}

export function modifyProperty<T extends AnyObject, KEY extends keyof T & string = 'uid'>(
  array: T[] | null,
  propertyNameToReplace: KEY,
  targetValue: T[KEY],
  uniqueValues: string[],
  // @ts-ignore assumption T contains uid
  uniqueValueKey: KEY = 'uid'
): T[] {
  if (!array) {
    return [];
  }
  const data = clone(array);
  uniqueValues.forEach((uid) => {
    const entry = getEntryBy(data, uid, uniqueValueKey);
    if (entry) {
      entry[propertyNameToReplace] = targetValue;
    }
  });
  return [...data];
}

export function removeElements<T extends AnyObject, KEY extends keyof T & string = 'uid'>(
  array: T[],
  uniqueValues: string[],
  // @ts-ignore assumption T contains uid
  uniqueValueKey: KEY = 'uid'
): T[] {
  const result: T[] = clone(array);

  uniqueValues.forEach((uid) => {
    result.splice(getIndexBy(uid, result, uniqueValueKey), 1);
  });
  return result;
}

export function removeElementByIndex<T extends AnyObject>(array: T[], index: number): T[] {
  const result: T[] = clone(array);
  result.splice(index, 1);
  return result;
}

export function replaceElement<T extends AnyObject, KEY extends keyof T & string = 'uid'>(
  array: T[],
  newElement: T,
  // @ts-ignore assumption T contains uid
  uniqueValueKey: KEY = 'uid'
): T[] {
  const result: T[] = clone(array);
  if (newElement[uniqueValueKey]) {
    const index = getIndexBy(newElement[uniqueValueKey], array, uniqueValueKey);
    if (index !== -1) {
      result.splice(index, 1, clone(newElement));
    }
  } else {
    throw new Error(`No "${uniqueValueKey}" property in object: ${JSON.stringify(newElement)}`);
  }
  return result;
}

export function replaceElementByIndex<T>(array: T[], newElement: T, index: number): T[] {
  const result: T[] = clone(array);
  if (index !== -1) {
    result.splice(index, 1, clone(newElement));
  }
  return result;
}

export const sortAlphabetically = <T extends {}, KEY extends keyof T>(array: T[], sortableStringProperty: KEY): T[] =>
  array.sort((a, b) => {
    const x = `${a[sortableStringProperty]}`.toLowerCase();
    const y = `${b[sortableStringProperty]}`.toLowerCase();
    if (x < y) {
      return -1;
    }
    if (x > y) {
      return 1;
    }
    return 0;
  });

function clone<D>(data: D): D {
  return cloneDeep(data);
}
